import {
  ButtonBase,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Paper,
  Typography,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useContext, useEffect, useState } from "react";
import {
  alertReport,
  capacityAllocationReport,
  demandDisaggregation,
  demandLossReport,
  getDemandSummaryReport,
  getUploadFilters,
  historicalDemandReport,
  systemSourcingMatrixReport,
} from "LTCP Module/api/ltcpBackend";
import { LTCPContext } from "LTCP Module/LTCPContext";
import { useMsal } from "@azure/msal-react";

const widthVal = {
  width: "32%",
  minWidth: "32%",
  maxWidth: "32%",
  height: 48,
  display: "flex",
  alignItems: "center",
  px: 2,
  py: 1,
  boxSizing: "border-box",
};

const widthValSubFile = {
  width: "24%",
  minWidth: "24%",
  maxWidth: "24%",
  height: 48,
  display: "flex",
  alignItems: "center",
  px: 2,
  py: 1,
  boxSizing: "border-box",
};

const widthValDownload = {
  width: "40%",
  minWidth: "40%",
  maxWidth: "40%",
  height: 48,
  display: "flex",
  alignItems: "center",
  px: 2,
  py: 1,
  boxSizing: "border-box",
};


// This is a stateless blank card component that we will use as placeholer
function BlankCard() {
  return <Paper sx={{ ...widthValSubFile }}></Paper>;
}


// This is a stateless label card that will show the given text value in
// the card
function TextCard({ text }) {
  return (
    <Paper sx={{ ...widthVal }}>
      <Typography fontSize={14}>{text}</Typography>
    </Paper>
  );
}


// This is a statelss drop down card and it will show the currently selected
// value based on the given state parameter and if the user change something
// in the drop down it will update the value in the state as well using the
// given set state function.
function DropDownCard({ value, setValue, options }) {
  return (
    <Paper sx={{ ...widthValSubFile }}>
      <Select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        sx={{
          fontSize: 14,
          width: "100%",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
      >
        {options.map((item, index) => (
          <MenuItem key={index} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </Paper>
  );
}


// This is a statelss card with a single download button that will call the
// given call back function when the user will click on that download button.
function DownloadButton({ onClick }) {
  return (
    <Paper sx={{ ...widthValDownload, px: 2, py: 1, boxSizing: "border-box" }}>
      <ButtonBase
        onClick={onClick}
        sx={{
          display: "flex",
          direction: "row",
          alignItems: "center",
          gap: 1,
          color: "blue",
        }}
      >
        <FileDownloadIcon fontSize="small" />
        Download
      </ButtonBase>
    </Paper>
  );
}


// This is a statelss card with a double download button that will call the
// specific call back function when the user will click on that download
// button. If the hide second parameter is true then it will hide the second
// download button.
function DoubleDownloadButton({
  name1,
  onClick1,
  name2,
  onClick2,
  hideSecond,
}) {
  return (
    <Paper sx={{ ...widthValDownload, px: 2, py: 1, boxSizing: "border-box" }}>
      <Stack direction="row" gap={2}>
        <ButtonBase
          onClick={onClick1}
          sx={{
            display: "flex",
            direction: "row",
            alignItems: "center",
            gap: 1,
            color: "blue",
            textAlign: "left",
          }}
        >
          <FileDownloadIcon fontSize="small" />
          {name1}
        </ButtonBase>
        {!hideSecond && (
          <ButtonBase
            onClick={onClick2}
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "center",
              gap: 1,
              color: "blue",
              textAlign: "left",
            }}
          >
            <FileDownloadIcon fontSize="small" />
            {name2}
          </ButtonBase>
        )}
      </Stack>
    </Paper>
  );
}


// This is the page component of the report page that will show all the
// report specific rows with the labels and download buttons and based on the
// report it will show dropdown or blank card in between.
function ReportPage() {
  const navigate = useNavigate();
  const {
    productType,
    setProductType,
    runtime,
    runtype,
    setRuntype,
    setRuntime,
    allRunDetails,
    runDetails,
    setRunDetails,
    runTimeOptions,
    setRunTimeOptions,
    demandFile,
    setDemandFile,
    demandOptions,
    setDemandOptions,
  } = useContext(LTCPContext);
  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const [userEmail, setUserEmail] = useState("");
  const [demandDisaggState, setDemandDisaggState] = useState("V1Demand");
  const [demandSummaryState, setDemandSummaryState] = useState("national");
  const [capacityState, setCapacityState] = useState("allocation");
  const [demandLossState, setDemandLossState] = useState("V1Demand");

  function changeRunType(val) {
    let run_details = allRunDetails[val];
    let new_prod_type = Object.keys(run_details)[0];
    let demand_options = run_details[new_prod_type];
    let new_demand_type = Object.keys(demand_options)[0];
    let run_times = demand_options[new_demand_type];

    setRuntype(val);
    setProductType(new_prod_type);
    setDemandFile(new_demand_type);
    setRuntime(run_times[0]);

    setRunDetails(run_details);
    setDemandOptions(demand_options);
    setRunTimeOptions(run_times);

    if (val === "Schedule") {
      setProductType("All");
    } else {
      setProductType(new_prod_type);
    }
  }

  function changeProductType(val) {
    let demand_options = runDetails[val];
    let default_demand_type = Object.keys(demand_options)[0];
    let run_times = demand_options[default_demand_type];

    setProductType(val);
    setDemandFile(default_demand_type);
    setDemandOptions(demand_options);
    setRunTimeOptions(run_times);
    setRuntime(run_times[0]);
  }

  function changeDemandType(val) {
    let run_times = demandOptions[val];

    setDemandFile(val);
    setRunTimeOptions(run_times);
    setRuntime(run_times[0]);
  }

  function changeRunTime(val) {
    setRuntime(val);
  }

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      setUserEmail(account.username);
    }
  }, []);

  return (
    <Stack p={4} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row">
          <IconButton onClick={() => navigate(-1)}>
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography fontWeight="bold" fontSize={21}>
            Reporting
          </Typography>
        </Stack>
        <Stack gap={2} direction="row" alignItems="center">
          <Typography fontSize={12}>Runtype</Typography>
          <Select
            size="small"
            sx={{ background: "#fff", height: 28, width: 150 }}
            value={runtype}
            onChange={(e) => changeRunType(e.target.value)}
          >
            {Object.keys(allRunDetails).map((item, index) => (
              <MenuItem value={item} key={index}>
                {item}
              </MenuItem>
            ))}
          </Select>
          <Typography fontSize={12}>Product Type</Typography>
          <Select
            size="small"
            sx={{ background: "#fff", height: 28, width: 150 }}
            value={productType}
            disabled={runtype === "Schedule"}
            onChange={(e) => changeProductType(e.target.value)}
          >
            {runtype === "Schedule" && <MenuItem value="All">All</MenuItem>}
            {Object.keys(runDetails).map((item, index) => (
              <MenuItem value={item} key={index}>
                {item.replaceAll("_", " ")}
              </MenuItem>
            ))}
          </Select>
          <Typography fontSize={12}>Demand Type</Typography>
          <Select
            size="small"
            sx={{ background: "#fff", height: 28, width: 150 }}
            value={demandFile}
            onChange={(e) => changeDemandType(e.target.value)}
          >
            {Object.keys(demandOptions).map((item, index) => (
              <MenuItem value={item} key={index}>
                {item.replaceAll("_", " ")}
              </MenuItem>
            ))}
          </Select>
          <Typography fontSize={12}>Runtime</Typography>
          <Select
            size="small"
            sx={{ background: "white", height: 28, width: 150 }}
            value={runtime}
            onChange={(e) => changeRunTime(e.target.value)}
          >
            {runTimeOptions.map((item, index) => (
              <MenuItem key={index} value={item}>
                {new Date(item).toLocaleString()}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>
      <Stack width="100%" gap={1}>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <Box sx={{ ...widthVal }}>
            <Typography fontSize={14}>File</Typography>
          </Box>
          <Box sx={{ ...widthValSubFile }}>
            <Typography fontSize={14}>Sub-File</Typography>
          </Box>
          <Box sx={{ ...widthValDownload }}>
            <Typography fontSize={14}>Download</Typography>
          </Box>
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Demand Disaggregation" />
          <BlankCard />
          <DownloadButton
            onClick={() =>
              demandDisaggregation({
                producttype: productType,
                demandtype: demandFile,
                runtime,
                runtype,
                user_email: userEmail,
              })
            }
          />
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Demand Summary" />
          <DropDownCard
            value={demandSummaryState}
            setValue={setDemandSummaryState}
            options={[
              { value: "national", label: "National" },
              { value: "branch", label: "Branch" },
              { value: "cluster", label: "Cluster" },
            ]}
          />
          <DownloadButton
            onClick={() =>
              getDemandSummaryReport({
                runtype,
                runtime,
                demand_type: demandFile,
                time_gran: demandSummaryState,
                user_email: userEmail,
                product_type: productType,
              })
            }
          />
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Capacity" />
          <DropDownCard
            value={capacityState}
            setValue={setCapacityState}
            options={[
              { value: "allocation", label: "Shift Allocation" },
              { value: "year", label: "CU% Yearly" },
              { value: "quarter", label: "CU% Quarterly" },
              { value: "month", label: "CU% Monthly" },
            ]}
          />
          <DownloadButton
            onClick={() =>
              capacityAllocationReport({
                producttype: productType,
                time_gran: capacityState,
                demand_type: demandFile,
                runtype,
                runtime,
                user_email: userEmail,
              })
            }
          />
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Demand Loss" />
          <BlankCard />
          <DoubleDownloadButton
            name1="Download Factory Loss"
            name2="Download Cluster / Depot Loss"
            hideSecond={demandFile === "V1Demand"}
            onClick1={() =>
              demandLossReport({
                producttype: productType,
                demandtype: demandFile,
                runtime,
                runtype,
                report: "Factory",
                user_email: userEmail,
              })
            }
            onClick2={() =>
              demandLossReport({
                producttype: productType,
                demandtype: demandFile,
                runtime,
                runtype,
                report: "CD",
                user_email: userEmail,
              })
            }
          />
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Alerts" />
          <BlankCard />
          <DownloadButton
            onClick={() =>
              alertReport({
                runtime,
                runtype,
                producttype: productType,
                user_email: userEmail,
                demand_type: demandFile,
              })
            }
          />
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Historical Demand Data" />
          <BlankCard />
          <DownloadButton
            onClick={() =>
              historicalDemandReport({
                producttype: productType,
                runtype,
                runtime,
                user_email: userEmail,
                demandtype: demandFile,
              })
            }
          />
        </Stack>
        <Stack gap={2} direction="row" justifyContent="space-between">
          <TextCard text="Sourcing Matrix" />
          <BlankCard />
          <DownloadButton onClick={systemSourcingMatrixReport} />
        </Stack>
      </Stack>
    </Stack>
  );
}


export default ReportPage;
